/**
 * Shifts HTTP client.
 *
 * IMPORTANT — PDF download:
 *   PDFs go through axios with responseType: "blob" so the JWT + every
 *   other request header set by interceptors (X-Business-Id, etc.) is
 *   carried. The old `zReportPdfUrl(id)` helper that returned a string
 *   URL is DEPRECATED — it produced relative URLs (resolving against
 *   the Vite dev origin, not the backend) and bypassed auth headers.
 *
 *   Use `openZReportPdf(id)` to open in a new tab, or
 *       `downloadZReportPdf(id, filename)` to trigger a save dialog.
 */
import api from "./api";
import type {
  Shift,
  ShiftsListResponse,
  ShiftsListFilters,
  OpenShiftInput,
  CloseShiftInput,
} from "@/types/shifts.types";

function unwrap<T>(res: { data: unknown }): T {
  const body = res.data as { success?: boolean; data?: T } | T;
  if (
    body &&
    typeof body === "object" &&
    "data" in (body as Record<string, unknown>) &&
    "success" in (body as Record<string, unknown>)
  ) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export const shiftsService = {
  async getCurrent(businessId?: string): Promise<Shift | null> {
    // Always the signed-in person's own shift (cashier, staff or a manager
    // at the till) — the day-close screens use the list instead.
    const params: Record<string, string> = { mine: "1" };
    if (businessId) params.businessId = businessId;
    const res = await api.get("/shifts/current", { params });
    const body = res.data as Shift | null;
    return body || null;
  },

  async open(input: OpenShiftInput): Promise<Shift> {
    const res = await api.post("/shifts/open", input);
    return unwrap<Shift>(res);
  },

  async close(id: string, input: CloseShiftInput): Promise<Shift> {
    const res = await api.post(`/shifts/${id}/close`, input);
    return unwrap<Shift>(res);
  },

  async forceClose(id: string, input: CloseShiftInput): Promise<Shift> {
    const res = await api.post(`/shifts/${id}/force-close`, input);
    return unwrap<Shift>(res);
  },

  async list(filters: ShiftsListFilters = {}): Promise<ShiftsListResponse> {
    const params: Record<string, string> = {};
    if (filters.businessId) params.businessId = filters.businessId;
    if (filters.cashierUserId) params.cashierUserId = filters.cashierUserId;
    if (filters.status) params.status = filters.status;
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.limit) params.limit = String(filters.limit);
    if (filters.mine) params.mine = "1";
    const res = await api.get("/shifts", { params });
    return unwrap<ShiftsListResponse>(res);
  },

  async get(id: string): Promise<Shift> {
    const res = await api.get(`/shifts/${id}`);
    return unwrap<Shift>(res);
  },

  async getZReport(id: string): Promise<Shift> {
    const res = await api.get(`/shifts/${id}/z-report`);
    return unwrap<Shift>(res);
  },

  /**
   * Open the Z-report PDF in a new tab.
   *
   * Goes through axios so the JWT + X-Business-Id header are attached.
   * Converts the response blob to an object URL and opens it. The URL
   * is revoked after 30s to free memory.
   */
  /** What should be in the drawer now (null when blind close hides it) + drops/payouts/pay-ins. */
  async drawer(id: string): Promise<DrawerState> {
    const res = await api.get<{ data: DrawerState }>(`/shifts/${id}/drawer`);
    return res.data.data;
  },

  async addCashMovement(id: string, input: CashMovementInput): Promise<CashMovement> {
    const res = await api.post<{ data: CashMovement }>(`/shifts/${id}/cash-movements`, input);
    return res.data.data;
  },

  async openZReportPdf(id: string): Promise<void> {
    const res = await api.get(`/shifts/${id}/z-report.pdf`, {
      responseType: "blob",
    });
    const blob = new Blob([res.data as BlobPart], {
      type: "application/pdf",
    });
    const url = URL.createObjectURL(blob);
    // Open in new tab. Most browsers honor the inline content-disposition
    // and render the PDF directly.
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  },

  /**
   * Trigger a "Save As…" download of the Z-report PDF.
   */
  async downloadZReportPdf(id: string, filename: string): Promise<void> {
    const res = await api.get(`/shifts/${id}/z-report.pdf`, {
      responseType: "blob",
    });
    const blob = new Blob([res.data as BlobPart], {
      type: "application/pdf",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  },
};

export default shiftsService;

export type CashMovementKind = "drop" | "payout" | "pay_in";

export interface CashMovement {
  _id: string;
  kind: CashMovementKind;
  amountMinor: number;
  destination?: "safe" | "bank" | null;
  category?: string | null;
  note?: string;
  createdByName?: string;
  approvedByName?: string;
  createdAt: string;
}

export interface CashMovementInput {
  kind: CashMovementKind;
  amountMinor: number;
  destination?: "safe" | "bank";
  category?: string;
  note?: string;
  managerPin?: string;
  idempotencyKey?: string;
}

export interface DrawerState {
  shiftId: string;
  status: "open" | "closed";
  blind: boolean;
  expectedCashMinor: number | null;
  parts: {
    openingMinor: number;
    cashTakenMinor: number;
    cashRefundsMinor: number;
    payInsMinor: number;
    dropsMinor: number;
    payoutsMinor: number;
  } | null;
  movements: CashMovement[];
  payoutCategories: string[];
  openedAt?: string;
  shiftNumber?: string;
  summary?: {
    salesCount: number;
    netRevenueMinor: number;
    refundsCount: number;
    collectionsCount: number;
    momoTakenMinor: number;
    momoRefundsMinor: number;
  };
}
